import type { CSSResult } from 'lit'
import { html, render } from 'lit'

export type ScopedLightDomCSSResult = CSSResult & {
  readonly __nordScopedLightDomCSSResult: true
}

function isDocument(node: Node): node is Document {
  return node.nodeType === Node.DOCUMENT_NODE
}

export const lightDomScopeAttribute = 'data-nord-scope'

/**
 * Stamps the stable stylesheet key on explicitly supplied component-owned
 * nodes. Callers must never pass consumer-owned children: ownership cannot be
 * inferred safely by traversing a light-DOM host.
 */
export function setLightDomScope(key: string, ...ownedElements: Element[]): void {
  if (!key || /[\t\n\f\r ]/.test(key)) {
    throw new TypeError('Light DOM scope keys must be non-empty whitespace-free tokens')
  }

  for (const element of ownedElements) {
    const scopes = new Set(
      (element.getAttribute(lightDomScopeAttribute) || '').split(/\s+/).filter(Boolean),
    )
    scopes.add(key)
    element.setAttribute(lightDomScopeAttribute, Array.from(scopes).join(' '))
  }
}

/**
 * Injects a stylesheet for a light-DOM custom element into its nearest root
 * (the document `<head>` or the enclosing shadow root).
 *
 * Light-DOM components can't scope styles with `:host`/shadow boundaries, so we
 * render a single `<style data-component="tag-name">` element per tag, per root.
 * Every caller host adds that stable key to its whitespace-separated
 * `data-nord-scope` token set, even when the stylesheet already exists.
 * Subsequent instances in the same root reuse the existing stylesheet, and a
 * host using more than one sheet retains every scope required by those sheets.
 *
 * Pass `key` to share one stylesheet across several tags (e.g. a stylesheet with
 * combined selectors): the first caller injects it under that key and later
 * callers (of any allowed tag) reuse both the stylesheet and stable family
 * scope. Defaults to the host's tag name.
 *
 * Mirrors the original pattern in `Table.ts`.
 */
function injectStyles(host: HTMLElement, styles: CSSResult, key: string): void {
  setLightDomScope(key, host)

  const rootNode = host.getRootNode() as Document | ShadowRoot
  const renderTarget = isDocument(rootNode) ? rootNode.head : rootNode

  const alreadyInjected = Array.from(renderTarget.children).some(
    element => element instanceof HTMLStyleElement && element.dataset.component === key,
  )
  if (alreadyInjected) {
    return
  }

  const fragment = document.createDocumentFragment()
  render(html`<style data-component=${key}>${styles}</style>`, fragment)
  renderTarget.appendChild(fragment)
}

/** Injects a manifest-registered `*.light-dom.css` stylesheet. */
export function injectLightDomStyles(
  host: HTMLElement,
  styles: ScopedLightDomCSSResult,
  key: string = host.localName,
): void {
  injectStyles(host, styles, key)
}

/**
 * Compatibility path for explicitly grandfathered light-DOM stylesheets.
 * New code must use a registered `*.light-dom.css` sheet and
 * `injectLightDomStyles` instead.
 */
export function injectLegacyLightDomStyles(
  host: HTMLElement,
  styles: CSSResult,
  key: string = host.localName,
): void {
  injectStyles(host, styles, key)
}
